/**
 * Convert a simple markdown subset (used in blog articles) into safe HTML.
 *
 * Supported syntax:
 *  - "## Heading"
 *  - "**bold paragraph**"
 *  - "*italic quote paragraph*"
 *  - "![alt](url)" image
 *  - "- item" unordered list
 *  - "1. item" ordered list
 *  - default: paragraph
 *
 * Output is sanitized with DOMPurify to mitigate XSS.
 */
import DOMPurify from 'dompurify';

function renderListItems(paragraph, prefixRegex) {
  return paragraph
    .split('\n')
    .map((item) => `<li class="mb-2">${item.replace(prefixRegex, '')}</li>`)
    .join('');
}

function renderImage(paragraph) {
  const match = paragraph.match(/!\[(.*?)\]\((.*?)\)/);
  if (!match) return null;
  return `<img src="${match[2]}" alt="${match[1]}" class="w-full h-auto rounded-lg shadow-lg my-8" />`;
}

function renderBlock(paragraph) {
  if (paragraph.startsWith('## ')) {
    return `<h2 class="text-3xl font-bold text-notaria-primary mt-12 mb-6">${paragraph.replace('## ', '')}</h2>`;
  }
  if (paragraph.startsWith('**') && paragraph.endsWith('**')) {
    return `<p class="text-xl font-bold text-gray-900 my-6">${paragraph.replace(/\*\*/g, '')}</p>`;
  }
  if (paragraph.startsWith('*') && paragraph.endsWith('*')) {
    return `<p class="italic text-gray-600 border-l-4 border-notaria-secondary pl-4 my-6">${paragraph.replace(/\*/g, '')}</p>`;
  }
  if (paragraph.startsWith('![')) {
    const img = renderImage(paragraph);
    if (img) return img;
  }
  if (paragraph.startsWith('- ')) {
    return `<ul class="list-disc pl-6 my-6 space-y-2">${renderListItems(paragraph, /^- /)}</ul>`;
  }
  if (paragraph.match(/^\d\./)) {
    return `<ol class="list-decimal pl-6 my-6 space-y-2">${renderListItems(paragraph, /^\d\.\s/)}</ol>`;
  }
  return `<p class="text-lg my-4">${paragraph}</p>`;
}

export function renderArticleHTML(content) {
  if (!content) return '';
  const rawHtml = content.split('\n\n').map(renderBlock).join('');
  // Sanitize against XSS; ALLOW the markup our renderer produces.
  return DOMPurify.sanitize(rawHtml, {
    ALLOWED_TAGS: ['h2', 'h3', 'p', 'ul', 'ol', 'li', 'img', 'a', 'strong', 'em', 'br'],
    ALLOWED_ATTR: ['class', 'href', 'src', 'alt', 'target', 'rel'],
    ALLOW_DATA_ATTR: false,
  });
}

/**
 * Sanitize rich HTML produced by the CMS editor (contentEditable output).
 * Mitigates XSS while allowing common formatting tags.
 */
export function sanitizeHTML(html) {
  if (!html) return '';
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ['h1', 'h2', 'h3', 'h4', 'p', 'ul', 'ol', 'li', 'img', 'a',
      'strong', 'b', 'em', 'i', 'u', 'br', 'blockquote', 'span', 'div'],
    ALLOWED_ATTR: ['class', 'href', 'src', 'alt', 'target', 'rel'],
    ALLOW_DATA_ATTR: false,
  });
}
